// The leader's Work centres view: every station on the lines in scope, grouped
// by department, each card carrying every line it runs.
//
// A department is a row of cards, and a card is a work centre — not a tile per
// line, which put a station on four lines in four places and made "how is
// Plumbing doing" a search. The word on the card is the worst of its lines;
// the lines underneath say which. A card whose station has an unanswered andon
// grows two buttons, because that is the one state a leader can clear from
// here: "Go to work centre", and the verb.
//
// THE VERB IS "TAKE IT" WITH THE PREVIEW ON (frame v2.12 script L880:
// `verb: 'Take it', onVerb: ackAndon(an)`, which opens the ETA sheet), and it
// opens that sheet over this list. Without the preview it is the "Ack ↗"
// link into the Andon app, which owns the clock there.

import { useState } from 'react';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { FlowlineRow } from '@/components/home/Score';
import { TakeSheet, takeTargetOf } from '@/components/andon/TakeSheet';
import type { AndonVM } from '@/api/types';
import { useT } from '@/i18n/useT';
import { andonHref, useFlowlineShort, useUnacknowledgedAndons } from '@/queries';
import { useDepartmentGroups } from '@/queries/leader';
import type { StationTone } from '@/lib/stationTone';
import { toneWord, SectionHead } from '@/screens/phone/HomeScreen';
import { useStore } from '@/store';
import { Icon } from '@/components/ui/Icon';

const TONE_COLOUR: Record<StationTone['kind'], string> = {
  danger: 'var(--danger)',
  warn: 'var(--warn)',
  pos: 'var(--pos)',
  grey: 'var(--line)',
};

export function FloorStations({
  flowlineIds,
  onDrillTo,
}: {
  flowlineIds: number[];
  onDrillTo: (workcentreId: number, name: string) => void;
}) {
  const t = useT();
  const departmentId = useStore((s) => s.leaderDepartmentId);
  const setDepartment = useStore((s) => s.setLeaderDepartment);
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const connMode = useStore((s) => s.connMode);
  const openAndon = useStore((s) => s.openAndon);
  const { groups, departments, isPending } = useDepartmentGroups(flowlineIds, departmentId);
  const unack = useUnacknowledgedAndons();
  const short = useFlowlineShort();
  const [taking, setTaking] = useState<AndonVM | null>(null);
  // Taking writes an Odoo user onto the record; a PIN session is the service
  // account and names nobody, so its cards carry no responder verb.
  const canTake = andonPreview && connMode === 'user';

  // The unanswered andon at a station, when there is one: the card's verb.
  const unackAt = (workcentreId: number) => (unack.data ?? []).find((a) => a.workcentreId === workcentreId) ?? null;

  return (
    <>
      {departments.length > 1 && (
        <div role="group" aria-label={t.areaT} style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
          <DeptChip selected={departmentId == null} onClick={() => setDepartment(null)}>
            {t.allAreasT}
          </DeptChip>
          {departments.map((d) => (
            <DeptChip key={d.id} selected={departmentId === d.id} onClick={() => setDepartment(d.id)}>
              {d.name}
            </DeptChip>
          ))}
        </div>
      )}

      {isPending && groups.length === 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 8 }}>
          <Skeleton height={110} radius={13} />
          <Skeleton height={110} radius={13} />
          <Skeleton height={110} radius={13} />
        </div>
      ) : groups.length === 0 ? (
        <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
          <EmptyState>{t.noStationsRunningT}</EmptyState>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {groups.map((g) => (
            <section key={g.department?.id ?? 'none'} aria-label={g.name}>
              <SectionHead
                label={`${g.name} · ${g.workcentres.length}`}
                sub={g.needYou ? `${g.needYou} ${t.needYouCountT}` : ''}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 8 }}>
                {g.workcentres.map(({ workcentre, pairs, worst }) => {
                  const andon = unackAt(workcentre.id);
                  const colour = TONE_COLOUR[worst.kind];
                  return (
                    <div
                      key={workcentre.id}
                      style={{
                        background: 'var(--surface)',
                        border: `1px solid ${worst.kind === 'danger' || worst.kind === 'warn' ? colour : 'var(--line)'}`,
                        borderRadius: 13,
                        padding: '10px 12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <button
                        type="button"
                        className="press-row"
                        onClick={() => onDrillTo(workcentre.id, workcentre.name)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 28, textAlign: 'left', width: '100%' }}
                      >
                        <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: colour, flex: 'none' }} />
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            font: '700 13px Manrope,sans-serif',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {workcentre.name}
                        </span>
                        <span style={{ font: '700 10.5px Manrope,sans-serif', color: worst.kind === 'grey' ? 'var(--sub)' : colour, whiteSpace: 'nowrap' }}>
                          {worst.kind === 'grey' ? '' : toneWord(worst.word, worst.behind, t)}
                        </span>
                        <span aria-hidden="true" style={{ color: 'var(--sub)', display: 'flex' }}>
                          <Icon name="chevron" size={12} />
                        </span>
                      </button>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {pairs.map((p) => (
                          <FlowlineRow
                            key={p.state.flowlineId}
                            small
                            short={short(p.state.flowlineId)}
                            done={p.done}
                            target={p.target}
                            dueNow={p.dueNow}
                            delta={p.tone.delta}
                            chip=""
                            chipTone="neutral"
                            exText={p.tone.kind === 'pos' ? '' : toneWord(p.tone.word, p.tone.behind, t)}
                            exTone={p.tone.kind === 'grey' ? 'grey' : p.tone.kind}
                          />
                        ))}
                      </div>
                      {andon && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            className="press"
                            onClick={() => onDrillTo(workcentre.id, workcentre.name)}
                            style={{ flex: 1, minHeight: 38, borderRadius: 10, font: '700 12px Manrope,sans-serif', border: '1px solid var(--line)', color: 'var(--ink)' }}
                          >
                            {t.goToWorkCentreT}
                          </button>
                          {andonPreview ? (
                            canTake && (
                              <button
                                type="button"
                                className="press"
                                onClick={() => setTaking(andon)}
                                style={{ flex: 1, minHeight: 38, borderRadius: 10, font: '700 12px Manrope,sans-serif', background: 'var(--ink)', color: 'var(--bg)' }}
                              >
                                {t.verbTakeItT}
                              </button>
                            )
                          ) : (
                            <a
                              href={andonHref(andonAppUrl, andon.id)}
                              target="_blank"
                              rel="noopener"
                              className="press"
                              style={{
                                flex: 1,
                                minHeight: 38,
                                borderRadius: 10,
                                font: '700 12px Manrope,sans-serif',
                                background: 'var(--ink)',
                                color: 'var(--bg)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 5,
                                textDecoration: 'none',
                              }}
                            >
                              {t.ackT}
                              <Icon name="external" size={12} />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
      <TakeSheet target={taking ? takeTargetOf(taking) : null} onClose={() => setTaking(null)} onOpenThread={openAndon} />
    </>
  );
}

function DeptChip({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className="press"
      aria-pressed={selected}
      onClick={onClick}
      style={{
        flex: 'none',
        borderRadius: 999,
        padding: '6px 12px',
        minHeight: 30,
        font: `${selected ? 700 : 600} 11px Manrope,sans-serif`,
        background: selected ? 'var(--ink)' : 'var(--surface)',
        border: `1px solid ${selected ? 'var(--ink)' : 'var(--line)'}`,
        color: selected ? 'var(--bg)' : 'var(--sub)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}
